import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { of } from 'rxjs';
import { filter, map, switchMap, withLatestFrom } from 'rxjs/operators';

import { GlobalMessageConfig } from '../config/global-message-config';
import { GlobalMessage } from '@boels/models';
import {
  countOfDeepEqualObjects,
  indexOfFirstOccurrence,
  isNotUndefined,
} from '@boels/helpers';
import { defaultGlobalMessageConfigFactory } from '../config/default-global-message-config';
import { addMessage, removeMessage } from '../actions/global-message.actions';
import { GlobalMessageState } from '../reducers/global-message.reducer';
import { getGlobalMessageEntitiesByType } from '../selectors/global-message.selectors';

@Injectable({
  providedIn: 'root',
})
export class GlobalMessageEffect {
  private actions$ = inject(Actions);
  private store = inject<Store<GlobalMessageState>>(Store);
  private platformId = inject(PLATFORM_ID);

  removeDuplicated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addMessage),
      map((action) => action.message),
      switchMap((message: GlobalMessage) =>
        of(message.text).pipe(
          withLatestFrom(
            this.store.pipe(
              select(getGlobalMessageEntitiesByType(message.type)),
            ),
          ),
          filter(
            ([text, messages]: [string, string[]]) =>
              countOfDeepEqualObjects(text, messages) > 1,
          ),
          map(([text, messages]: [string, string[]]) => {
            const index = indexOfFirstOccurrence(text, messages);
            if (index !== undefined) {
              return removeMessage({
                messageType: message.type,
                index,
              });
            }
            return undefined;
          }),
          filter(isNotUndefined),
        ),
      ),
    ),
  );

  config: GlobalMessageConfig;

  constructor() {
    this.config = defaultGlobalMessageConfigFactory();
  }
}
